import { AvailableDevices, MediaDeviceInfoType, SelectedDevices, State } from '../types/Video';
import { ReactComponent as MicrophoneNotFoundSvg } from 'assets/icons/microphoneNotFound.svg';
import { ReactComponent as MicrophoneMutedSvg } from 'assets/icons/microphoneMuted.svg';
import { ReactComponent as CameraNotFound } from 'assets/icons/cameraNotFound.svg';
import { ReactComponent as RotateCameraSvg } from 'assets/icons/arrowRepeat.svg';
import { ReactComponent as CameraMutedSvg } from 'assets/icons/cameraMuted.svg';
import { ReactComponent as MicrophoneSvg } from 'assets/icons/microhpone.svg';
import { ReactComponent as SettingsSvg } from 'assets/icons/settings.svg';
import { ReactComponent as DialPadSvg } from 'assets/icons/dialPad.svg';
import { ReactComponent as PersonSvg } from 'assets/icons/person.svg';
import { ReactComponent as CameraSvg } from 'assets/icons/camera.svg';
import { ReactComponent as PhoneSvg } from 'assets/icons/phone.svg';
import { ReactComponent as BlurSvg } from 'assets/icons/blur.svg';

import { PermissionWarning } from '../shared/PermissionWarning';
import DeviceDropdown from '../shared/DeviceDropdown';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../shared/LanguageSwitcher';
import { poweredByLink } from '../shared/variables';
import {
  SettingsModalTitle,
  RotateCameraButton,
  SettingsButton,
  SettingsModal,
  InCallWrapper,
  ToggleButton,
  DialPadModal,
  RemoteVideo,
  WaitingOverlay,
  WaitingDots,
  BlurButton,
  LocalVideo,
  Disconnect,
  PoweredBy,
  Controls,
  Content,
  Middle,
  Center,
  Right,
  Media,
  Left,
} from './InCallStyle';

type InCallProps = {
  changeCurrentState: (state: State) => void;
  localVideoTrack: MediaStreamTrack | null;
  localAudioTrack: MediaStreamTrack | null;
  remoteVideoTrack: MediaStreamTrack | null;
  remoteAudioTrack: MediaStreamTrack | null;
  microphoneMuted: boolean;
  cameraMuted: boolean;
  toggleMicrophone: () => void;
  toggleCamera: () => void;
  updateDevice: (device: MediaDeviceInfoType) => void;
  availableDevices?: AvailableDevices;
  selectedDevices?: SelectedDevices;
  isVideoBlurred: boolean;
  applyBlurOnInCallVideo: () => void;
  TurnCameraOnInCallVideo: () => void;
  isUpgrade: boolean;
  handleDisconnect: () => Promise<void>;
  vonageSession?: any;
  vonagePublisher?: any;
  vonageRemoteStream?: any;
  sendDtmf?: (digit: string) => void;
  callStatus?: 'active' | 'hold' | 'transfer';
  cameraPermissionDenied?: boolean;
};

export const InCall = ({
  changeCurrentState,
  localVideoTrack,
  localAudioTrack,
  remoteVideoTrack,
  remoteAudioTrack,
  microphoneMuted,
  cameraMuted,
  toggleMicrophone,
  toggleCamera,
  updateDevice,
  availableDevices,
  selectedDevices,
  isVideoBlurred,
  applyBlurOnInCallVideo,
  TurnCameraOnInCallVideo,
  isUpgrade,
  handleDisconnect,
  vonageSession,
  vonagePublisher,
  vonageRemoteStream,
  sendDtmf,
  callStatus = 'active',
  cameraPermissionDenied,
}: InCallProps) => {
  const { t } = useTranslation();
  const isVonage = !!vonageSession;
  const [dialPadInputValue, setDialPadInputValue] = useState<string>('');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showDialPadModal, setShowDialPadModal] = useState(false);
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const settingsModalRef = useRef<HTMLDivElement>(null);
  const dialPadModalRef = useRef<HTMLDivElement>(null);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);
  const dialPadButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isVonage && localVideoRef.current) {
      const mediaStream = new MediaStream();

      if (localVideoTrack) {
        mediaStream.addTrack(localVideoTrack);
      }

      if (localAudioTrack) {
        mediaStream.addTrack(localAudioTrack);
      }

      if (mediaStream.getTracks().length > 0) {
        localVideoRef.current.srcObject = mediaStream;
        localVideoRef.current.play().catch((err) => {
          console.error('Error playing video:', err);
        });
      } else {
        localVideoRef.current.srcObject = null;
        if (!cameraMuted && !microphoneMuted) {
          console.warn('No tracks available to display - check camera/microphone permissions');
        }
      }
    }
  }, [localVideoTrack, localAudioTrack, isVonage]);

  useEffect(() => {
    if (!isVonage && remoteVideoRef.current && remoteVideoTrack && remoteAudioTrack) {
      const mediaStream = new MediaStream([remoteVideoTrack, remoteAudioTrack]);
      remoteVideoRef.current.srcObject = mediaStream;
      remoteVideoRef.current.play();
      setShowDialPadModal(false);
    }
  }, [remoteVideoTrack, remoteAudioTrack, isVonage]);

  const handleDeviceSelect = (device: MediaDeviceInfoType) => {
    setShowSettingsModal(false);
    updateDevice(device);
  };

  useEffect(() => {
    const handleClick = (event: any) => {
      if (settingsModalRef.current && !settingsModalRef.current.contains(event.target)) {
        setShowSettingsModal(false);
      }
      if (dialPadModalRef.current && !dialPadModalRef.current.contains(event.target)) {
        setShowDialPadModal(false);
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [settingsModalRef, dialPadModalRef]);

  const generateDialPad = () => {
    const dialItems = [];

    for (let i = 1; i <= 9; i++) {
      dialItems.push(
        <button onClick={() => handleDialPadItemClick(i.toString())} key={i}>
          {i}
        </button>,
      );
    }

    dialItems.push(
      <button onClick={() => handleDialPadItemClick('*')} key="*">
        *
      </button>,
      <button onClick={() => handleDialPadItemClick('0')} key="0">
        0
      </button>,
      <button onClick={() => handleDialPadItemClick('#')} key="#">
        #
      </button>,
    );

    return dialItems;
  };

  const handleDialPadItemClick = (item: string) => {
    setDialPadInputValue((value) => value + item);
    sendDtmf?.(item);
  };

  useEffect(() => {
    if (isVideoBlurred) {
      applyBlurOnInCallVideo();
    }
  }, []);

  useEffect(() => {
    if (showSettingsModal && settingsModalRef.current) {
      const firstFocusable = settingsModalRef.current.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      firstFocusable?.focus();
    }
  }, [showSettingsModal]);

  // Dial pad modal focus
  useEffect(() => {
    if (showDialPadModal && dialPadModalRef.current) {
      const input = dialPadModalRef.current.querySelector<HTMLInputElement>('input');
      input?.focus();
    }
  }, [showDialPadModal]);

  return (
    <InCallWrapper>
      <Center>
        <Content>
          <Media>
            <RemoteVideo id={isVonage ? 'remoteVideoInCall' : undefined}>
              {!isVonage && <video ref={remoteVideoRef} autoPlay playsInline={true} muted={false} />}
              {((!isVonage && !remoteVideoTrack) || (isVonage && !vonageRemoteStream)) && (
                <WaitingOverlay>
                  <PersonSvg />
                  <p>{t('inCall.waitingForConsultant')}</p>
                  <WaitingDots>
                    <span />
                    <span />
                    <span />
                  </WaitingDots>
                </WaitingOverlay>
              )}
              {isVonage && callStatus !== 'active' && (
                <WaitingOverlay>
                  <p>{t(callStatus === 'hold' ? 'inCall.onHold' : 'inCall.connectingSpecialist')}</p>
                  <WaitingDots>
                    <span />
                    <span />
                    <span />
                  </WaitingDots>
                </WaitingOverlay>
              )}
            </RemoteVideo>
            <LocalVideo $isVideoBlurred={isVideoBlurred} id={isVonage ? 'localVideoInCall' : undefined}>
              {!isVonage && (
                <div>
                  <video ref={localVideoRef} autoPlay playsInline={true} muted={true} id={'localVideoInCall'} />
                  <canvas id="localVideoInCallCanvas" className={'hide'}></canvas>
                  {!localVideoTrack?.enabled && <PersonSvg />}
                </div>
              )}
              <PermissionWarning show={cameraPermissionDenied} />
            </LocalVideo>
            <BlurButton
              onClick={() => applyBlurOnInCallVideo()}
              $isVideoBlurred={isVideoBlurred}
              aria-label={!isVideoBlurred ? t('blur.turnOn') : t('blur.turnOff')}
            >
              <BlurSvg />
            </BlurButton>
            <RotateCameraButton onClick={() => TurnCameraOnInCallVideo()} aria-label={t('inCall.switchCamera')}>
              <RotateCameraSvg />
            </RotateCameraButton>
          </Media>
          <Controls>
            <Left></Left>
            <Middle>
              <ToggleButton
                ref={dialPadButtonRef}
                $muted={showDialPadModal}
                aria-label={t('inCall.dialPad')}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDialPadModal(!showDialPadModal);
                  setShowSettingsModal(false);
                }}
              >
                <DialPadSvg />
              </ToggleButton>
              {showDialPadModal && (
                <DialPadModal ref={dialPadModalRef} role="dialog" aria-modal="true" aria-label={t('inCall.dialPad')}>
                  <div>
                    <input value={dialPadInputValue} />
                  </div>
                  {generateDialPad()}
                </DialPadModal>
              )}
              <ToggleButton
                $muted={microphoneMuted}
                onClick={() => toggleMicrophone()}
                aria-label={microphoneMuted ? t('microphone.unmute') : t('microphone.mute')}
                aria-pressed={microphoneMuted}
              >
                {availableDevices?.audioInputs.length === 0 ? (
                  <MicrophoneNotFoundSvg />
                ) : microphoneMuted ? (
                  <MicrophoneMutedSvg />
                ) : (
                  <MicrophoneSvg />
                )}
              </ToggleButton>
              <Disconnect onClick={() => handleDisconnect()} aria-label={t('inCall.endCall')}>
                <PhoneSvg />
              </Disconnect>
              <ToggleButton
                $muted={cameraMuted}
                onClick={() => toggleCamera()}
                aria-label={cameraMuted ? t('camera.turnOn') : t('camera.turnOff')}
                aria-pressed={cameraMuted}
              >
                {availableDevices?.videoInputs.length === 0 ? (
                  <CameraNotFound />
                ) : cameraMuted ? (
                  <CameraMutedSvg />
                ) : (
                  <CameraSvg />
                )}
              </ToggleButton>
              <SettingsButton>
                <ToggleButton
                  ref={settingsButtonRef}
                  $muted={showSettingsModal}
                  aria-label={t('inCall.settings')}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSettingsModal(!showSettingsModal);
                    setShowDialPadModal(false);
                  }}
                >
                  <SettingsSvg />
                </ToggleButton>
              </SettingsButton>
              {showSettingsModal && (
                <SettingsModal
                  ref={settingsModalRef}
                  role="dialog"
                  aria-modal="true"
                  aria-label={t('inCall.settings')}
                >
                  <SettingsModalTitle>{t('inCall.settings')}</SettingsModalTitle>
                  {availableDevices?.audioOutputs && (
                    <DeviceDropdown
                      availableDevices={availableDevices.audioInputs}
                      selectedDevice={selectedDevices?.audioInput}
                      label={t('devices.microphone')}
                      onSelect={handleDeviceSelect}
                    />
                  )}
                  {availableDevices?.audioOutputs && (
                    <DeviceDropdown
                      availableDevices={availableDevices.audioOutputs}
                      selectedDevice={selectedDevices?.audioOutput}
                      label={t('devices.speaker')}
                      onSelect={handleDeviceSelect}
                    />
                  )}
                  {availableDevices?.audioOutputs && (
                    <DeviceDropdown
                      availableDevices={availableDevices.videoInputs}
                      selectedDevice={selectedDevices?.videoInput}
                      label={t('devices.camera')}
                      onSelect={handleDeviceSelect}
                    />
                  )}
                </SettingsModal>
              )}
            </Middle>
            <Right $isVideoBlurred={isVideoBlurred}>
              <BlurButton
                onClick={() => applyBlurOnInCallVideo()}
                $isVideoBlurred={isVideoBlurred}
                aria-label={!isVideoBlurred ? t('blur.turnOn') : t('blur.turnOff')}
              >
                <BlurSvg />
              </BlurButton>
            </Right>
          </Controls>
          <PoweredBy>
            <a href={poweredByLink} target="_blank" aria-label={`${t('common.poweredBy')} ${t('common.opensInNewTab')}`}>
              {t('common.poweredBy')}
              <span className="sr-only">{t('common.opensInNewTab')}</span>
            </a>
            <LanguageSwitcher />
          </PoweredBy>
        </Content>
      </Center>
    </InCallWrapper>
  );
};
